import { useState } from 'react';
import type { ListItem } from '@travel-planner/shared';
import { useMe } from '../auth/useAuth';
import { useAddComment, useComments, useDeleteComment, useVote } from './useSocial';

export function VoteRow({
  item,
  tripId,
  countryId,
}: {
  item: ListItem;
  tripId: string;
  countryId: string;
}) {
  const me = useMe();
  const vote = useVote(tripId, countryId);
  const mine = me.data?.username;

  const agreed = item.voteJoel && item.voteLea;
  const split = item.voteJoel !== item.voteLea;

  const pill = (user: 'joel' | 'lea', on: boolean) => {
    const editable = mine === user;
    return (
      <button
        key={user}
        className="badge rounded-pill border-0"
        disabled={!editable || vote.isPending}
        onClick={() => vote.mutate({ itemId: item.id, value: !on })}
        title={editable ? 'Toggle your vote' : `${user}'s vote`}
        style={{
          fontSize: '0.7rem',
          border: '1px solid',
          borderColor: on ? 'var(--ok)' : 'var(--border)',
          background: on ? 'var(--ok)' : 'var(--surface)',
          color: on ? '#06210f' : 'var(--text-muted)',
          cursor: editable ? 'pointer' : 'default',
        }}
      >
        {user === 'joel' ? 'Joel' : 'Lea'} {on ? '✓' : ''}
      </button>
    );
  };

  return (
    <span className="d-inline-flex gap-1 align-items-center">
      {pill('joel', item.voteJoel)}
      {pill('lea', item.voteLea)}
      {agreed && <span className="text-success" style={{ fontSize: '0.7rem' }}>agreed</span>}
      {split && <span className="text-warning" style={{ fontSize: '0.7rem' }}>⚠ split</span>}
    </span>
  );
}

export function CommentThread({ tripId, itemId }: { tripId: string; itemId: string }) {
  const me = useMe();
  const comments = useComments(tripId, itemId, true);
  const add = useAddComment(tripId, itemId);
  const del = useDeleteComment(tripId, itemId);
  const [body, setBody] = useState('');

  return (
    <div className="mt-2 pt-2" style={{ borderTop: '1px dashed var(--border)' }}>
      {(comments.data ?? []).map((c) => (
        <div key={c.id} className="small mb-1">
          <strong>{c.authorUsername === 'joel' ? 'Joel' : 'Lea'}</strong>{' '}
          <span className="text-body-tertiary" style={{ fontSize: '0.7rem' }}>
            {new Date(c.createdAt).toLocaleString()}
          </span>
          <button
            className="btn btn-ghost btn-sm text-danger p-0 ms-2"
            onClick={() => del.mutate(c.id)}
            title="Delete"
            style={{ fontSize: '0.7rem', minHeight: 'auto', lineHeight: 1 }}
          >
            ✕
          </button>
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.body}</div>
        </div>
      ))}
      <form
        className="d-flex gap-2 mt-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim() || !me.data) return;
          add.mutate(body.trim(), { onSuccess: () => setBody('') });
        }}
      >
        <input
          className="form-control form-control-sm"
          placeholder="Add a comment…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button type="submit" className="btn btn-secondary btn-sm" disabled={!body.trim() || add.isPending}>
          Send
        </button>
      </form>
    </div>
  );
}
